import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { WORDS as GLOSSES } from "../lib/tokiPonaData";
import {
  CONSONANTS,
  VOWELS,
  ILLEGAL_SYLLABLES,
  MARKS,
  BRIEFS,
  DISPLACED,
  WORDS,
  seatOf,
  parseSyllable,
  specToLatin,
  legalSyllables,
  syllableDistractors,
  wordDistractors,
  shuffleWith,
} from "../lib/sitelenSike";
import {
  SyllableGlyph,
  WordGlyph,
  ScriptText,
  GlyphCard,
} from "../components/sitelenSike/Glyphs";
import "../styles/sitelenSike.css";

// Friendly names for each consonant shape and vowel rotation, used by the
// anatomy panel so a glyph can be read as "shape, turned for its vowel".
const CONS_NAMES = {
  p: ["open curve", "a big C opening to the right"],
  m: ["looped curve", "the open curve wearing a loop — a loop anywhere means a nasal"],
  w: ["ticked curve", "the open curve with a little tick tucked in its mouth"],
  t: ["key", "a straight stem with a bar poking out sideways"],
  n: ["pennant", "a stem flying a looped flag — the loop is the nasal"],
  s: ["seven", "along the top, then a slash down"],
  l: ["hook", "a stem curling over at its foot"],
  k: ["chevron", "two strokes meeting at a point, opening right"],
  j: ["chevron + tail", "the chevron with a small tail kicked off its lower arm"],
  "": ["comma", "a lone curl that stands in for “no consonant”"],
};

const VOWEL_NAMES = {
  a: ["home", "the shape exactly as drawn"],
  e: ["points down", "turned so it points or opens downward"],
  i: ["flipped", "home turned fully upside-down"],
  o: ["points up", "turned so it points or opens upward"],
  u: ["home + dot", "home position, plus a dot off its right side"],
};

const STATS_KEY = "sitelen-sike-stats-v1";

function loadStats() {
  try {
    return JSON.parse(localStorage.getItem(STATS_KEY)) || {};
  } catch {
    return {};
  }
}

function saveStats(stats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch {
    // private mode etc. — practice still works, it just doesn't persist
  }
}

// ---------------------------------------------------------------- learn tab

function ReferenceChart({ selected, onSelect }) {
  const cols = VOWELS.split("");
  const rows = CONSONANTS.split("");
  return (
    <div className="ss-chartbox">
      <div
        className="ss-chart-grid"
        style={{ gridTemplateColumns: `24px repeat(${cols.length}, minmax(0, 1fr))` }}
      >
        <span />
        {cols.map((v) => (
          <span key={v} className="ss-chart-head">
            {v}
          </span>
        ))}
        {rows.map((c) => (
          <React.Fragment key={c}>
            <span className="ss-chart-head">{c}</span>
            {cols.map((v) => {
              const banned = ILLEGAL_SYLLABLES.includes(c + v);
              const spec = { cons: c, vowel: v, coda: false };
              const isSel = selected && selected.cons === c && selected.vowel === v;
              return (
                <button
                  key={v}
                  type="button"
                  className={`ss-chartcell${banned ? " banned" : ""}${isSel ? " selected" : ""}`}
                  disabled={banned}
                  onClick={() => onSelect(spec)}
                  aria-label={banned ? `${c + v} (never occurs)` : `syllable ${c + v}`}
                >
                  <SyllableGlyph
                    spec={spec}
                    size={40}
                    color={banned ? "var(--ss-line)" : undefined}
                  />
                  <span className="ss-latin">{banned ? "—" : c + v}</span>
                </button>
              );
            })}
          </React.Fragment>
        ))}
        <span className="ss-chart-head">–</span>
        {cols.map((v) => {
          const spec = { cons: "", vowel: v, coda: false };
          const isSel = selected && selected.cons === "" && selected.vowel === v;
          return (
            <button
              key={v}
              type="button"
              className={`ss-chartcell${isSel ? " selected" : ""}`}
              onClick={() => onSelect(spec)}
              aria-label={`bare vowel ${v}`}
            >
              <SyllableGlyph spec={spec} size={40} />
              <span className="ss-latin">{v}</span>
            </button>
          );
        })}
      </div>
      <p className="ss-hint">
        greyed syllables (ji, ti, wo, wu) never occur in toki pona. bottom row: the comma
        carrier for bare vowels. click any cell to break it down.
      </p>
    </div>
  );
}

function AnatomyPanel({ spec }) {
  const latin = specToLatin(spec);
  return (
    <div className="ss-anatomy">
      <SyllableGlyph spec={spec} size={86} />
      <div className="ss-anatomy-info">
        <b className="ss-mono">{latin}</b> = the <b>{CONS_NAMES[spec.cons][0]}</b>
        {spec.cons ? (
          <>
            {" "}
            for <span className="ss-mono">{spec.cons}</span> ({CONS_NAMES[spec.cons][1]})
          </>
        ) : (
          <> ({CONS_NAMES[spec.cons][1]})</>
        )}
        , at <b>{VOWEL_NAMES[spec.vowel][0]}</b> for{" "}
        <span className="ss-mono">{spec.vowel}</span> — {VOWEL_NAMES[spec.vowel][1]}.
        {(spec.vowel === "e" || spec.vowel === "o") && (
          <>
            {" "}
            The reading rule: at <span className="ss-mono">e</span> everything points{" "}
            <b>down</b>, at <span className="ss-mono">o</span> everything points <b>up</b> —
            whichever way the shape has to turn to obey.
          </>
        )}
      </div>
    </div>
  );
}

function KeyBrowser({ selected, onSelect }) {
  const keys = Object.keys(BRIEFS);
  return (
    <div className="ss-keygrid">
      {keys.map((key) => {
        const ladder = BRIEFS[key];
        return (
          <button
            key={key}
            type="button"
            className={`ss-keycard${selected === key ? " selected" : ""}`}
            onClick={() => onSelect(key)}
          >
            <SyllableGlyph spec={parseSyllable(key)} size={34} />
            <span className="ss-keycard-words">
              {ladder.map((w, i) => (
                <span key={w} className={`${i === 0 ? "bare" : ""} ${DISPLACED[w] ? "rescued" : ""}`}>
                  {w}
                </span>
              ))}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function LadderDetail({ ladder }) {
  return (
    <div className="ss-examples">
      {BRIEFS[ladder].map((w, i) => (
        <div key={w} className="ss-example">
          <WordGlyph word={w} size={52} />
          <span className="ss-latin">{w}</span>
          <span className="ss-example-caption">
            {MARKS[i]}
            {DISPLACED[w] ? " · rescued †" : ""}
          </span>
        </div>
      ))}
    </div>
  );
}

function LearnTab() {
  const [selected, setSelected] = useState({ cons: "t", vowel: "o", coda: false });
  const [key, setKey] = useState("si");
  return (
    <>
      <div className="ss-panel">
        <h2>how the script works</h2>
        <p className="ss-prose">
          sitelen sike writes each syllable as one round glyph: a <b>shape</b> for the
          consonant, <b>rotated</b> for its vowel. At home position you're reading{" "}
          <span className="ss-mono">a</span>; turned to point down it's{" "}
          <span className="ss-mono">e</span>; flipped it's <span className="ss-mono">i</span>;
          pointing up it's <span className="ss-mono">o</span>; and{" "}
          <span className="ss-mono">u</span> is home wearing a dot. A final{" "}
          <span className="ss-mono">-n</span> is a bar under the glyph, written inside the
          syllable.
        </p>
        <h3>the letterforms</h3>
        <ReferenceChart selected={selected} onSelect={setSelected} />
        <AnatomyPanel spec={selected} />
        <h3>codas</h3>
        <div className="ss-examples">
          <div className="ss-example">
            <SyllableGlyph spec={{ cons: "k", vowel: "o", coda: true }} size={52} />
            <span className="ss-latin">kon</span>
            <span className="ss-example-caption">
              final <b>-n</b> = a bar under the glyph
            </span>
          </div>
          <div className="ss-example">
            <WordGlyph word="tenpo" size={52} />
            <span className="ss-latin">tenpo</span>
            <span className="ss-example-caption">
              the coda stays inside its own syllable — this is the <b>ten</b> glyph
            </span>
          </div>
        </div>
      </div>

      <div className="ss-panel">
        <h2>briefs: one glyph per word</h2>
        <p className="ss-prose">
          Common words don't get spelled out — each one sits on its <b>first syllable</b>,
          and up to four words share a syllable via a ladder of marks:{" "}
          <b>bare</b>, <b className="ss-m1-text">+M1</b> (a dot above),{" "}
          <b className="ss-m2-text">+M2</b> (a slash below), or both — assigned by
          frequency, most common word first. If a syllable is itself a word (like{" "}
          <span className="ss-mono">pi</span>), it keeps the bare seat.
        </p>
        <LadderDetail ladder={key} />
        <h3>the whole table</h3>
        <KeyBrowser selected={key} onSelect={setKey} />
        <p className="ss-hint">
          † rescued words: their first-syllable ladder was full, so they brief on a later
          syllable of the word instead (awen → wen, akesi → ke, alasa → sa, pimeja → me,
          oko → ko).
        </p>
      </div>

      <div className="ss-panel">
        <h2>everything else</h2>
        <div className="ss-examples">
          <div className="ss-example">
            <WordGlyph word="kokosila" size={44} />
            <span className="ss-latin">kokosila</span>
            <span className="ss-example-caption">
              unknown words are <b>spelled</b> syllable by syllable under an overline
            </span>
          </div>
          <div className="ss-example">
            <WordGlyph word="Sonja" size={44} />
            <span className="ss-latin">Sonja</span>
            <span className="ss-example-caption">
              names are spelled inside a <b>cartouche</b> ring
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------- read tab

const rng = Math.random;

function makeSyllableQuestion() {
  const deck = legalSyllables();
  const pick = deck[Math.floor(rng() * deck.length)];
  // Sometimes drill the -n coda so the bar gets practiced too.
  const answer = rng() < 0.25 ? { ...pick, coda: true } : pick;
  const choices = shuffleWith(
    [answer, ...syllableDistractors(answer, rng)].map(specToLatin),
    rng
  );
  return { kind: "syllable", answer, answerLatin: specToLatin(answer), choices };
}

function makeWordQuestion() {
  const answer = WORDS[Math.floor(rng() * WORDS.length)];
  const choices = shuffleWith([answer, ...wordDistractors(answer, rng)], rng);
  return { kind: "word", answer, answerLatin: answer, choices };
}

function seatHint(word) {
  const seat = seatOf(word);
  if (!seat) return null;
  return `${seat.key} ${MARKS[seat.mark]}${DISPLACED[word] ? " · rescued brief!" : ""}`;
}

function ReadTab() {
  const [mode, setMode] = useState("syllable");
  const [question, setQuestion] = useState(() => makeSyllableQuestion());
  const [picked, setPicked] = useState(null);
  const [streak, setStreak] = useState(0);
  const [stats, setStats] = useState(loadStats);
  const timerRef = useRef(null);

  const next = useCallback((m = mode) => {
    clearTimeout(timerRef.current);
    setPicked(null);
    setQuestion(m === "syllable" ? makeSyllableQuestion() : makeWordQuestion());
  }, [mode]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const switchMode = (m) => {
    setMode(m);
    setStreak(0);
    next(m);
  };

  const answered = picked !== null;
  const modeStats = stats[mode] || { best: 0, seen: 0, correct: 0 };

  const choose = (choice) => {
    if (answered) return;
    setPicked(choice);
    const right = choice === question.answerLatin;
    const nextStreak = right ? streak + 1 : 0;
    setStreak(nextStreak);
    const s = {
      ...stats,
      [mode]: {
        best: Math.max(modeStats.best, nextStreak),
        seen: modeStats.seen + 1,
        correct: modeStats.correct + (right ? 1 : 0),
      },
    };
    setStats(s);
    saveStats(s);
    if (right) timerRef.current = setTimeout(() => next(), 1000);
  };

  const gloss = question.kind === "word" ? GLOSSES[question.answer]?.g : null;

  return (
    <div className="ss-panel">
      <div className="ss-modes">
        <button
          type="button"
          className={`ss-tab${mode === "syllable" ? " active" : ""}`}
          onClick={() => switchMode("syllable")}
        >
          syllables
        </button>
        <button
          type="button"
          className={`ss-tab${mode === "word" ? " active" : ""}`}
          onClick={() => switchMode("word")}
        >
          whole words
        </button>
      </div>
      <div className="ss-stats">
        <span>streak {streak}</span>
        <span>best {Math.max(modeStats.best, streak)}</span>
        <span>
          {modeStats.correct}/{modeStats.seen} all-time
        </span>
      </div>

      <GlyphCard>
        {question.kind === "syllable" ? (
          <SyllableGlyph spec={question.answer} size={92} />
        ) : (
          <WordGlyph word={question.answer} size={92} />
        )}
      </GlyphCard>

      <div className="ss-choices">
        {question.choices.map((c) => {
          let cls = "ss-choice";
          if (answered) {
            if (c === question.answerLatin) cls += " correct";
            else if (c === picked) cls += " wrong";
            else cls += " dim";
          }
          return (
            <button key={c} type="button" className={cls} disabled={answered} onClick={() => choose(c)}>
              {c}
            </button>
          );
        })}
      </div>

      <div className="ss-feedback">
        {answered &&
          (picked === question.answerLatin ? (
            <span className="yay">sike pona! </span>
          ) : (
            <span className="nope">
              it reads <b className="ss-mono">{question.answerLatin}</b>.{" "}
            </span>
          ))}
        {answered && question.kind === "word" && (
          <span className="gloss">
            {seatHint(question.answer)}
            {gloss ? ` · ${question.answer} — ${gloss}` : ""}
          </span>
        )}
      </div>

      {answered && picked !== question.answerLatin && (
        <button type="button" className="ss-btn" onClick={() => next()}>
          next
        </button>
      )}
      {!answered && (
        <button type="button" className="ss-btn ghost" onClick={() => next()}>
          skip
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- write tab

function WriteTab() {
  const [text, setText] = useState("toki! mi olin e toki pona. jan Sonja li mama pi toki pona.");
  const [size, setSize] = useState(40);
  const [labels, setLabels] = useState(true);
  return (
    <div className="ss-panel">
      <h2>write something</h2>
      <div className="ss-board">
        {text.trim() ? (
          <ScriptText text={text} size={size} labels={labels} />
        ) : (
          <div className="ss-empty">type some toki pona below to see it written</div>
        )}
      </div>
      <div className="ss-inputrow">
        <label className="ss-label" htmlFor="ss-txt">
          toki pona text
        </label>
        <textarea
          id="ss-txt"
          spellCheck={false}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      </div>
      <div className="ss-controls">
        <div className="ss-ctrl">
          <label className="ss-label" htmlFor="ss-size">
            size
          </label>
          <input
            id="ss-size"
            type="range"
            min="24"
            max="72"
            step="2"
            value={size}
            onChange={(e) => setSize(parseInt(e.target.value, 10))}
          />
        </div>
        <label className="ss-toggle">
          <input type="checkbox" checked={labels} onChange={(e) => setLabels(e.target.checked)} />
          latin labels
        </label>
      </div>
      <p className="ss-hint">
        capitalized words become cartouche names. words in red aren't valid toki pona
        phonology. punctuation passes through in gold.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- page

const TABS = [
  { id: "learn", label: "learn" },
  { id: "read", label: "practice reading" },
  { id: "write", label: "write" },
];

export default function SitelenSike() {
  const [tab, setTab] = useState("learn");
  return (
    <div className="ss-root">
      <div className="ss-wrap">
        <header className="ss-header">
          <h1>sitelen sike</h1>
          <span className="ss-sub">toki pona round script · shape × rotation, one glyph per word</span>
          <Link className="ss-back" to="/sillyprojects">
            ← silly projects
          </Link>
        </header>
        <nav className="ss-tabs" aria-label="sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`ss-tab${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>
        {tab === "learn" && <LearnTab />}
        {tab === "read" && <ReadTab />}
        {tab === "write" && <WriteTab />}
      </div>
    </div>
  );
}
